import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createWallet,
  getWallets,
  getWalletByUserId,
  topUpWallet,
  confirmTopUp,
  releaseFunds,
  finalizeTransfer,
  createRecipient,
  fetchTransactions,
  getUserTransaction,
  getAllRecipients,
} from '../thunks/paymentThunks.ts';

// Define types for the state
interface WalletState {
  wallet: { balance: number } | null;
  wallets: any[];
  recipients: any[]; // This is where recipients are stored
  recipient: string | null;
  transaction: any | null;
  transactions: any[];
  loading: boolean;
  error: string | null;
  success: boolean;
}

const initialState: WalletState = {
  wallet: null,
  wallets: [],
  recipients: [], // Initial empty state for recipients
  recipient: null,
  transaction: null,
  transactions: [],
  loading: false,
  error: null,
  success: false,
};

const handlePending = (state: WalletState) => {
  state.loading = true;
  state.error = null;
};

const handleRejected = (state: WalletState, action: PayloadAction<any>) => {
  state.loading = false;
  state.error = action.payload;
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Wallet-related thunks
    builder
      .addCase(createWallet.pending, handlePending)
      .addCase(createWallet.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.wallet = action.payload;
      })
      .addCase(createWallet.rejected, handleRejected)

      .addCase(getWallets.pending, handlePending)
      .addCase(getWallets.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.wallets = action.payload;
      })
      .addCase(getWallets.rejected, handleRejected)

      .addCase(getWalletByUserId.pending, handlePending)
      .addCase(getWalletByUserId.fulfilled, (state, action: PayloadAction<{ balance: number }>) => {
        state.loading = false;
        state.wallet = action.payload;
      })
      .addCase(getWalletByUserId.rejected, handleRejected)

      .addCase(topUpWallet.pending, handlePending)
      .addCase(topUpWallet.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.wallet = action.payload;
      })
      .addCase(topUpWallet.rejected, handleRejected)

      .addCase(confirmTopUp.pending, handlePending)
      .addCase(confirmTopUp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(confirmTopUp.rejected, handleRejected);

    // Fund-related thunks
    builder
      .addCase(releaseFunds.pending, handlePending)
      .addCase(releaseFunds.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(releaseFunds.rejected, handleRejected);

    // Transaction-related thunks
    builder
      .addCase(getUserTransaction.pending, handlePending)
      .addCase(getUserTransaction.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.transaction = action.payload;
      })
      .addCase(getUserTransaction.rejected, handleRejected)

      .addCase(fetchTransactions.pending, handlePending)
      .addCase(fetchTransactions.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchTransactions.rejected, handleRejected);

    // Recipient-related thunks
    builder
      .addCase(createRecipient.fulfilled, (state, action: PayloadAction<any>) => {
        const { newRecipient } = action.payload;

        if (newRecipient) {
          state.recipients = [(state.recipients || []), newRecipient];
        } else {
          console.warn('No newRecipient found in payload:', action.payload);
        }
      })

      .addCase(getAllRecipients.pending, handlePending)
      .addCase(getAllRecipients.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.loading = false;
        state.recipients = Array.isArray(action.payload) ? action.payload : [];
      })

      .addCase(getAllRecipients.rejected, handleRejected);
  },
});

export default paymentSlice.reducer;
